<script setup lang="ts">
import Step0 from '@/components/auth/register/MultiStep/Step0.vue';
import Step1 from '@/components/auth/register/MultiStep/Step1.vue';
import Step2 from '@/components/auth/register/MultiStep/Step2.vue';
import Step3 from '@/components/auth/register/MultiStep/Step3.vue';
import Step4 from '@/components/auth/register/MultiStep/Step4.vue';
import type AntiRobot from '@/components/base/form/AntiRobot.vue';
import { useUserJourney } from '~/composables/apostatudo/use-user-journey';

const { ApiService } = useApiService();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { registerData, validationData } = storeToRefs(useRegisterStore());
const { $reset } = useRegisterStore();
const { openToaster } = useLayoutStore();
const { $metrics } = useNuxtApp();
const { t } = useTexts();
const { startSmartico } = useSmarticoInit();
const { setAuth } = useAuthStore();
const { isAvailableEnvironment: isNativeAppEnv } = useBiometricAuth();
const { rememberLastCredentials } = useBiometricEnrollment();
const { currentRouteName } = useRoutes();
const { currentRoute, push } = useRouter();
const { showModalImage } = useModalImage();
const { registerUserEvent } = useUserJourney();

const {
  isRegisterRequestDocument,
  isRegisterRequestBirthDate,
  isRequestAddressOnRegister,
  isRegisterRequestPhone,
  isMultiStepPreRegisterChoiceEnable,
  isAuthV2FlowEnabled,
  closeAuth,
} = useAuth();

const {
  isCaptchaRegisterEnabled,
  isCaptchaValidateCpfEnabled,
  isInvisibleCaptcha,
} = useCaptcha();

const {
  isSocialAuthFlow,
  socialEmail,
  socialToken,
  socialService,
  clearSocialData,
} = useAuthSocial();

const {
  referenceCode,
  utmSrc,
  utmSource,
  utmCampaign,
  utmContent,
  utmMedium,
  gaClientId,
} = useParameters();

const { isTaboolaEnabled, isKwaiPixelEnabled } = useFeatureFlagsEnv();
const { isLudopathyValidationEnabled } = useFeatureFlagsNew();

let steps: any = [];

if (isRequestAddressOnRegister.value) {
  steps = [Step0, Step1, Step2, Step3, Step4];
} else {
  steps = [Step0, Step1, Step3, Step4];
}

const currentStep = ref(isMultiStepPreRegisterChoiceEnable.value ? 0 : 1);
const isLoadingRegister = ref(false);
const antiRobotRef = ref<InstanceType<typeof AntiRobot> | null>(null);

const currentStepComponent = computed(() => steps[currentStep.value]);

const stepsTotalSize = steps.length - 1;

const isLastStep = computed(() => Number(currentStep.value) === stepsTotalSize);

const firstName = computed<string>(() => {
  return registerData.value.name?.split(' ')[0] ?? '';
});

const antiRobotToken = computed<string>(
  () => antiRobotRef.value?.finalCaptchaTokenValue ?? '',
);

const captchaConditions = computed<{
  hiddenMode: boolean;
  onlyAbsolute: boolean;
  withImage: boolean;
}>(() => {
  const hiddenMode =
    (!isCaptchaRegisterEnabled.value && !isCaptchaValidateCpfEnabled.value) ||
    (isCaptchaValidateCpfEnabled.value &&
      !isCaptchaRegisterEnabled.value &&
      validationData.value.fields.document);

  const onlyAbsolute = isInvisibleCaptcha.value && !hiddenMode;

  const withImage =
    !isInvisibleCaptcha.value &&
    isAuthV2FlowEnabled.value &&
    showModalImage.value &&
    !hiddenMode;

  return {
    hiddenMode,
    onlyAbsolute,
    withImage,
  };
});

const isNextStepDisabled = computed(() => {
  if (isLoadingRegister.value) {
    return true;
  }

  if (isCaptchaRegisterEnabled.value && !antiRobotToken.value) {
    return true;
  }

  const validationFields = validationData.value.fields;

  let currentFields: any = [];

  if (currentStep.value === 1) {
    currentFields = ['email', 'email_confirm', 'nationalities'];

    if (isRegisterRequestDocument.value) {
      currentFields.push('document', 'birth_date');
    } else if (isRegisterRequestBirthDate.value) {
      currentFields.push('birth_date');
    }
  } else if (currentStep.value === 2) {
    if (isRequestAddressOnRegister.value) {
      currentFields = [
        'name',
        'zipcode',
        'address',
        'city',
        'state',
        'country_data',
      ];
    } else {
      currentFields = ['password'];

      if (isRegisterRequestPhone.value) {
        currentFields.push('ddi', 'phone');
      }
    }
  } else if (currentStep.value === 3) {
    if (isRequestAddressOnRegister.value) {
      currentFields = ['password'];

      if (isRegisterRequestPhone.value) {
        currentFields.push('ddi', 'phone');
      }
    } else {
      currentFields = () =>
        !registerData.value.optIn.includes('termsAgreement') ||
        !registerData.value.optIn.includes('declarationOfTruth') ||
        (isLudopathyValidationEnabled.value &&
          !registerData.value.optIn.includes('ludopathy'));
    }
  } else if (currentStep.value === 4) {
    currentFields = () =>
      !registerData.value.optIn.includes('termsAgreement') ||
      !registerData.value.optIn.includes('declarationOfTruth') ||
      (isLudopathyValidationEnabled.value &&
        !registerData.value.optIn.includes('ludopathy'));
  }

  if (typeof currentFields === 'function') {
    return currentFields(); // Executa a função do passo 4
  }

  return (
    currentFields?.some(
      (field: string) =>
        !validationFields[field as keyof typeof validationFields],
    ) ?? false
  );
});

function setPrevStep(): void {
  if (currentStep.value > 0) {
    currentStep.value--;
  }

  resetAntiRobotToken();
}

function setNextStep() {
  if (isNextStepDisabled.value) {
    openToaster({
      message: t('auth.register.error.fill_the_fields'),
      type: 'error',
    });

    return;
  }

  if (Number(currentStep.value) === 1) {
    initRegister();
  } else if (isLastStep.value) {
    finishRegister();
  } else {
    patchRegister();
  }

  resetAntiRobotToken();
}

function handleError(err: any, fallbackMessageKey: string): void {
  console.debug(err);

  const errorMessage = err?.data?.message || t(fallbackMessageKey);

  openToaster({
    message: errorMessage,
    type: 'error',
  });
}

function handleSuccess(
  res: { status: number },
  successCallback: (res: any) => void,
): void {
  if (res.status.toString().startsWith('20')) {
    successCallback(res);
  } else {
    openToaster({
      message: t('auth.register.error.status_error'),
      type: 'error',
    });
  }
}

async function initRegister() {
  isLoadingRegister.value = true;

  const payload: IRegisterMultiStepInitData = {
    email: registerData.value.email,
    email_confirm: registerData.value.email_confirm,
    nationalities: JSON.stringify(registerData.value.nationalities),
    captcha_token: antiRobotToken.value,
  };

  if (isRegisterRequestDocument.value) {
    payload.cpf = registerData.value.document;
    payload.birth_date = registerData.value.birth_date;
  } else if (isRegisterRequestBirthDate.value) {
    payload.birth_date = registerData.value.birth_date;
  }

  if (isSocialAuthFlow.value) {
    payload.email = socialEmail.value;
    payload.email_confirm = socialEmail.value;
    payload.token = socialToken.value;
    payload.service = socialService.value;
  }

  console.debug(payload);

  try {
    const res = await ApiService.post(
      '/auth/preregister/step_initial',
      payload,
    );

    handleSuccess(res, () => {
      if (res.data) {
        registerData.value.userId = res.data.idUser;
        if (isRequestAddressOnRegister.value) {
          currentStep.value++;
        } else {
          currentStep.value = 2;
        }
      }
    });
  } catch (err) {
    handleError(err, 'auth.register.error.response_error');
  } finally {
    isLoadingRegister.value = false;
  }
}

async function patchRegister() {
  isLoadingRegister.value = true;

  const referenceCodeValue = referenceCode.value
    ? String(referenceCode.value)
    : String(registerData.value.affiliation_code);

  const payload: IRegisterMultiStepPatchData = {
    id: registerData.value.userId,
    captcha_token: antiRobotToken.value,
  };

  if (currentStep.value === 2) {
    payload.name = registerData.value.name;

    if (isRequestAddressOnRegister.value) {
      payload.zipcode = registerData.value.zipcode;
      payload.address = registerData.value.address;
      payload.city = registerData.value.city;
      payload.state = registerData.value.state;
      payload.country = registerData.value.country_data;
    } else {
      if (isRegisterRequestPhone.value) {
        payload.ddi = registerData.value.ddi;
        payload.phone = registerData.value.phone;
      }

      payload.password = registerData.value.password;
      payload.affiliation_code = referenceCodeValue;
    }
  } else {
    payload.password = registerData.value.password;
    payload.affiliation_code = referenceCodeValue;

    if (isRegisterRequestPhone.value) {
      payload.ddi = registerData.value.ddi;
      payload.phone = registerData.value.phone;
    }
  }

  if (isSocialAuthFlow.value) {
    payload.email = socialEmail.value;
    payload.email_confirm = socialEmail.value;
    payload.token = socialToken.value;
    payload.service = socialService.value;
    delete payload.password;
  }

  try {
    const res = await ApiService.patch(
      '/auth/preregister/step_complementary',
      payload,
    );

    handleSuccess(res, () => {
      if (res.data.success) {
        currentStep.value++;
      }
    });
  } catch (err) {
    handleError(err, 'auth.register.error.response_error');
  } finally {
    isLoadingRegister.value = false;
  }
}

async function finishRegister() {
  isLoadingRegister.value = true;

  const payload: IRegisterMultiStepFinishData = {
    id: registerData.value.userId,
    optIn: registerData.value.optIn,
    captcha_token: antiRobotToken.value,
  };

  if (isLudopathyValidationEnabled.value) {
    payload.ludopathy_term_accepted =
      registerData.value.optIn.includes('ludopathy');
  }

  if (utmSrc.value) {
    payload.src = utmSrc.value;
  }

  if (utmSource.value) {
    payload.utm_source = utmSource.value;
  }

  if (utmCampaign.value) {
    payload.utm_campaign = utmCampaign.value;
  }

  if (utmContent.value) {
    payload.utm_content = utmContent.value;
  }

  if (utmMedium.value) {
    payload.utm_medium = utmMedium.value;
  }

  if (gaClientId.value) {
    payload.ga_client_id = gaClientId.value;
  }

  if (isSocialAuthFlow.value) {
    payload.email = socialEmail.value;
    payload.email_confirm = socialEmail.value;
    payload.token = socialToken.value;
    payload.service = socialService.value;
  }

  try {
    const res = await ApiService.patch('/auth/preregister/step_end', payload);

    handleSuccess(res, async () => {
      openToaster({
        message: t('auth.register.success'),
        type: 'success',
      });

      clearSocialData();

      $metrics.push('gtm')?.handle({
        event: 'sign_up',
        ...gtmUserParams(res.data?.user),
      });

      $metrics.push('facebook')?.handle('track', 'Lead', {
        content_name: 'Cadastro',
        status: true,
        value: 1,
      });

      if (isKwaiPixelEnabled.value) {
        $metrics.push('kwai')?.handle('addToCart');
      }

      if (isTaboolaEnabled.value) {
        $metrics.push('taboola')?.handle('lead');
      }

      $metrics.push('appsflyer')?.handle('af_complete_registration', {
        af_registration_method: 'multi_step',
        _customer_user_id: res.data?.user?.id,
      });

      if (res.data) {
        if (window.self !== window.top) {
          window.parent.postMessage({
            message: 'loginSuccess',
            responseData: res.data,
            responseHeaders: {
              'X-INFOS-ID':
                res.headers.get('X-INFOS-ID') ||
                res.headers.get('X-Infos-Id') ||
                res.headers.get('x-infos-id'),
              'X-INFOS-REF':
                res.headers.get('X-INFOS-REF') ||
                res.headers.get('X-Infos-Ref') ||
                res.headers.get('x-infos-ref'),
            },
          });
        }

        if (isNativeAppEnv() && !isSocialAuthFlow.value) {
          rememberLastCredentials(
            registerData.value.email,
            registerData.value.password,
          );
        }

        await setAuth(res);
        registerUserEvent();

        if (currentRouteName.value === 'register') {
          await navigateTo(localeLink('/'));
        } else {
          push({
            query: {
              ...currentRoute.value.query,
            },
          });
        }

        closeAuth();

        startSmartico();

        $reset();
      }
    });
  } catch (err) {
    handleError(err, 'auth.register.error.api_error');
    isLoadingRegister.value = false;
  }
}

function resetAntiRobotToken() {
  antiRobotRef.value?.$resetAllCaptcha();
}

function setCurrentStep(step: number) {
  currentStep.value = step;
}

onMounted(() => {
  if (isAuthenticated.value) {
    navigateTo(localeLink('/'));
  }

  if (isSocialAuthFlow.value) {
    currentStep.value = 1;
  }
});

defineExpose({
  currentStep,
});
</script>

<template>
  <section :class="$style.register">
    <BaseLoading
      class="!bg-auth-bg-primary z-50"
      :icon-loader="true"
      :show="isLoadingRegister"
    />

    <div :class="$style.registerWrapper">
      <div :class="$style.registerInner">
        <AuthRegisterMultiStepProgress
          v-if="currentStep !== 0"
          :current-step="currentStep"
          :is-last-step="isLastStep"
          :steps="stepsTotalSize"
          :user-name="firstName"
        />

        <KeepAlive>
          <component
            :is="currentStepComponent"
            :anti-robot-token="antiRobotToken"
            :class="$style.multiStepForm"
            @reset-anti-robot-token="resetAntiRobotToken"
            @set-current-step="setCurrentStep"
          />
        </KeepAlive>
      </div>

      <ClientOnly>
        <Teleport
          v-if="currentStep !== 0"
          defer
          to="#dynamicContentFooterContainer"
        >
          <div
            :class="[
              $style.antiRobotContainer,
              { [$style.isOnlyAbsolute]: captchaConditions.onlyAbsolute },
              { [$style.isAbsoluteWithImage]: captchaConditions.withImage },
              { [$style.isHiddenMode]: captchaConditions.hiddenMode },
            ]"
          >
            <div :class="$style.captchaComponent">
              <BaseFormAntiRobot
                ref="antiRobotRef"
                :is-position-absolute="false"
              />
            </div>
          </div>
          <AuthRegisterMultiStepFooter
            :current-step="currentStep"
            :is-last-step="isLastStep"
            :next-step-disabled="isNextStepDisabled"
            :set-next-step="setNextStep"
            :set-prev-step="setPrevStep"
          />
        </Teleport>
      </ClientOnly>
    </div>
  </section>
</template>

<style module lang="postcss">
.register {
  @apply w-full flex flex-col min-h-full;
}

.registerWrapper {
  @apply w-full flex-grow overflow-auto flex flex-col pb-3;
}

.registerInner {
  @apply relative flex-grow;
}

.antiRobotContainer {
  @apply flex justify-center mx-auto px-4 whitespace-nowrap;

  &.isHiddenMode {
    @apply absolute opacity-0 -z-10 pointer-events-none mt-0 mb-0;
  }

  &.isOnlyAbsolute {
    @apply absolute items-center bottom-full bg-black/20 rounded-t-full left-1/2 -translate-x-1/2 max-h-12 overflow-hidden;
  }

  &.isAbsoluteWithImage {
    @apply md:absolute w-full items-center bottom-full md:-translate-x-full  lg:w-11/12;

    .captchaComponent {
      @apply w-full max-w-[320px] mx-auto;
    }
  }

  > * {
    @apply -mt-1 -mb-1.5;
  }
}
</style>
